import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { useViewerStore } from '@stores/viewerStore';
import { NETWORK_OFFLINE_MESSAGE } from './errors';
import type { RetryHandle } from './types';

const MAX_RETRY_COUNT = 3;

// ---------------------------------------------------------------------------
// 重试信号 — 外部 UI 触发重新加载
// ---------------------------------------------------------------------------

function createRetrySignal() {
  let version = 0;
  const listeners = new Set<() => void>();
  return {
    getSnapshot: () => version,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    retry: () => {
      version += 1;
      listeners.forEach((l) => l());
    },
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSceneRetry(): RetryHandle {
  const retrySignalRef = useRef<ReturnType<typeof createRetrySignal> | null>(null);
  if (retrySignalRef.current === null) {
    retrySignalRef.current = createRetrySignal();
  }
  const retrySignal = retrySignalRef.current;
  const retryCountRef = useRef(0);
  const retryVersion = useSyncExternalStore(retrySignal.subscribe, retrySignal.getSnapshot);

  const retry = useCallback(() => {
    retrySignal.retry();
  }, [retrySignal]);

  const resetRetryCount = useCallback(() => {
    retryCountRef.current = 0;
  }, []);

  const handleRetry = useCallback((): void => {
    if (!navigator.onLine) {
      const { setStatus, setLoadProgress } = useViewerStore.getState();
      resetRetryCount();
      setLoadProgress(null);
      setStatus('error', NETWORK_OFFLINE_MESSAGE);
      return;
    }

    if (retryCountRef.current < MAX_RETRY_COUNT) {
      retryCountRef.current += 1;
      retrySignal.retry();
    }
  }, [resetRetryCount, retrySignal]);

  return useMemo(
    () => ({
      retryVersion,
      retry,
      resetRetryCount,
      handleRetry,
    }),
    [handleRetry, resetRetryCount, retry, retryVersion],
  );
}
